import { useCallback } from 'react';
import { noop } from 'lodash';
import type { CreatedFolderFragment } from '@frameio/graphql-client/generated';
import { useCreateFolderMutation } from '@frameio/graphql-client/generated';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useFinderRefetch } from '@frameio/shared/contexts/FinderRefetchContext';
import { useLoadingToast } from '@frameio/shared/resources';
import { startEvent } from '@frameio/telemetry';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import { refetchFolderChildren } from '../cache-ops';
import TEXT from './generated/TEXT/useCreateFolder';

type CreateFolderOpts = {
  onSuccess?: (folder: CreatedFolderFragment) => void;
  showToast?: boolean;
};

type CreateFolderReturn = (args: {
  projectId: string;
  parentId: string;
  name?: string;
  restricted?: boolean;
}) => void;

const DEFAULT_OPTS: CreateFolderOpts = {
  onSuccess: noop,

  showToast: true,
};

/**
 * The useCreateFolder hook can be used to create a folder. It performs any
 * necessary cache updates.
 *
 * @param opts.onSuccess - an optional callback fired if the copy succeeds
 * @returns a function to call when ready to create the folder
 */
export function useCreateFolder(opts?: CreateFolderOpts): CreateFolderReturn {
  const accountId = useActiveAccountId();
  const { onSuccess, showToast } = {
    ...DEFAULT_OPTS,
    ...opts,
  };
  const [createFolder, { client }] = useCreateFolderMutation();
  const { addToast, updateAndHideToast } = useLoadingToast();
  const refetchFinder = useFinderRefetch();

  return useCallback(
    /**
     * @param projectId the id of the destination project
     * @param parentId the id of the parent folder
     * @param name the name of the folder, defaults to the localized default
     * folder name
     * @param restricted whether the folder should be restricted, defaults to false
     */
    ({
      projectId,
      parentId,
      name = TEXT.defaultFolderName,
      restricted = false,
    }: Parameters<CreateFolderReturn>[0]) => {
      const createFolderEvent = startEvent(CoreAssetsEventNames.CreateFolder, {
        name,
        project_id: projectId,
        parent_id: parentId,
      });

      let toastId: string;

      if (showToast) {
        toastId = addToast(TEXT.loading);
      }

      const handleError = () => {
        createFolderEvent.failed();

        if (showToast) {
          updateAndHideToast(toastId, {
            title: TEXT.errorMessage,
            variant: 'error',
          });
        }
      };

      createFolder({
        variables: {
          input: {
            parentId,
            name,
            restricted,
          },
        },

        onCompleted(response) {
          const asset = response.createFolder?.asset;

          if (asset) {
            createFolderEvent.completed();

            if (showToast) {
              updateAndHideToast(toastId, {
                title: TEXT.successMessage,
                variant: 'success',
              });
            }

            if (!asset || asset.__typename !== 'FolderAsset') return;

            refetchFinder();
            if (asset?.parent?.id)
              refetchFolderChildren(client, accountId, asset.parent.id);

            onSuccess?.(asset);
          } else {
            handleError();
          }
        },

        onError() {
          handleError();
        },
      });
    },
    [
      addToast,
      accountId,
      client,
      createFolder,
      onSuccess,
      refetchFinder,
      showToast,
      updateAndHideToast,
    ]
  );
}

TEXT.DEFINE({
  /** Default name given to a newly created folder */
  defaultFolderName: 'untitled folder',
  errorMessage: 'Failed to create folder.',
  loading: 'Creating folder…',
  successMessage: 'Folder created successfully.',
});
